/**
 * SpatialNavbar
 *
 * Premium dark navigation bar for spatial/campus mode.
 * Features company info, game speed controls, breadcrumb navigation, and financial display.
 */

import React, { useState } from 'react';
import { Settings, Pause, Play, Zap, Keyboard } from 'lucide-react';
import { getKeyboardShortcuts } from '../../../hooks/useAccessibleNavigation';
import { BreadcrumbNav } from './BreadcrumbNav';
import { useDeviceType } from '../../../hooks/useDeviceType';
import { useNavigationModeStore } from '../../../stores/navigationModeStore';
import { formatCompactCurrency } from '../../../utils/currencyFormatter';
import { useTutorialContext } from '../../../tutorial/TutorialProvider';
import type { CompanyState } from '../../../types/company';

interface GameControls {
  isPaused: boolean;
  gameSpeed: number;
}

interface GameSpeed {
  label?: string;
  name?: string;
  multiplier: number;
}

export interface SpatialNavbarProps {
  companyState: CompanyState;
  gameControls: GameControls;
  companyName?: string;
  onBack: () => void;
  onTogglePause?: () => void;
  onChangeSpeed?: (speed: GameSpeed) => void;
  availableSpeeds?: GameSpeed[];
}

// Shared dark panel classes for premium look
const PANEL = 'bg-gray-900/90 backdrop-blur-md border border-white/10 rounded-lg p-3';

export const SpatialNavbar: React.FC<SpatialNavbarProps> = ({
  companyState,
  gameControls,
  companyName,
  onBack,
  onTogglePause,
  onChangeSpeed,
  availableSpeeds,
}) => {
  const { deviceType, isMobile } = useDeviceType();
  const { mode } = useNavigationModeStore();
  const { isRunning: isTutorialRunning } = useTutorialContext();

  if (!companyState || !gameControls) {
    return (
      <div className="grid grid-cols-[minmax(200px,300px)_1fr_minmax(200px,300px)] gap-3 items-center">
        <div className={PANEL}>
          <div className="font-bold text-sm text-white/60">Loading...</div>
        </div>
        <div className={PANEL}>
          <div className="text-center text-white/40">Loading navigation...</div>
        </div>
        <div className={PANEL}>
          <div className="font-bold text-sm text-white/60">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[minmax(200px,300px)_1fr_minmax(200px,300px)] gap-3 items-center">
      {/* Company Info & Game Controls */}
      <div className={PANEL} data-tutorial="company-info">
        <div className="font-bold text-sm mb-2 text-white tracking-wide font-display">
          {companyName || 'Your Company'}
        </div>
        <div className="flex items-center gap-1">
          {onTogglePause && (
            <button
              onClick={!isTutorialRunning ? onTogglePause : undefined}
              data-testid="pause-button"
              disabled={isTutorialRunning}
              className={`w-7 h-7 rounded transition-colors flex items-center justify-center ${
                gameControls.isPaused
                  ? 'bg-theme-accent text-white'
                  : 'text-white/50 hover:text-white hover:bg-white/10'
              }`}
              title={gameControls.isPaused ? 'Resume' : 'Pause'}
            >
              {gameControls.isPaused ? (
                <Play className="w-3.5 h-3.5" />
              ) : (
                <Pause className="w-3.5 h-3.5" />
              )}
            </button>
          )}
          {onChangeSpeed &&
            availableSpeeds &&
            availableSpeeds
              .filter((speed) => speed.multiplier > 0)
              .map((speed) => {
                const isActive =
                  !gameControls.isPaused &&
                  gameControls.gameSpeed === speed.multiplier;
                const isMax = speed.multiplier === 8;
                return (
                  <button
                    key={speed.multiplier}
                    data-testid={`speed-${speed.multiplier}x`}
                    onClick={() => {
                      if (!isTutorialRunning) {
                        if (gameControls.isPaused && onTogglePause) {
                          onTogglePause();
                        }
                        onChangeSpeed(speed);
                      }
                    }}
                    className={`w-7 h-7 text-xs font-medium rounded transition-colors flex items-center justify-center ${
                      isTutorialRunning
                        ? 'text-white/20 cursor-not-allowed'
                        : isActive
                        ? 'bg-theme-accent text-white'
                        : 'text-white/50 hover:text-white hover:bg-white/10'
                    }`}
                    disabled={isTutorialRunning}
                    title={`${speed.label || speed.name || ''} Speed (${speed.multiplier}x)`}
                  >
                    {isMax ? (
                      <Zap className="w-3.5 h-3.5" />
                    ) : (
                      `${speed.multiplier}x`
                    )}
                  </button>
                );
              })}
        </div>
      </div>

      {/* Center Navigation */}
      <div className={PANEL}>
        <div className="flex justify-center">
          {mode === 'spatial' && !isMobile ? (
            <BreadcrumbNav compact={deviceType === 'tablet'} />
          ) : (
            <div className="text-sm text-white/40">
              Spatial Navigation
            </div>
          )}
        </div>
      </div>

      {/* Cash Info with Settings */}
      <div className={`${PANEL} overflow-hidden`} data-tutorial="cash-display">
        <div className="flex items-center justify-between min-w-0">
          <div className="text-left">
            <div className="text-xs text-white/50" data-testid="game-date">
              {new Date(
                companyState.year,
                companyState.month - 1,
                companyState.day
              ).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </div>
          </div>
          <div className="text-right flex-1">
            <div
              className="font-bold text-sm text-emerald-400 font-display"
              data-testid="cash-balance"
            >
              {companyState.cash > 100000000
                ? formatCompactCurrency(companyState.cash)
                : `$${companyState.cash.toLocaleString('en-US', {
                    maximumFractionDigits: 0,
                  })}`}
            </div>
            <div className="text-xs text-white/50">
              {formatCompactCurrency(companyState.dailyStats.profit * 30)} monthly
            </div>
          </div>
          <KeyboardHintsButton />
          <button
            onClick={!isTutorialRunning ? onBack : undefined}
            data-testid="settings-button"
            className={`ml-1 p-2 text-white/40 rounded transition-colors border border-white/10 ${
              isTutorialRunning
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:text-white hover:bg-white/10'
            }`}
            title="Settings & Menu"
            disabled={isTutorialRunning}
          >
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

/** Keyboard shortcut reference popover */
function KeyboardHintsButton() {
  const [open, setOpen] = useState(false);
  const shortcuts = getKeyboardShortcuts();

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="ml-2 p-2 text-white/40 rounded transition-colors border border-white/10 hover:text-white hover:bg-white/10"
        title="Keyboard Shortcuts"
      >
        <Keyboard className="w-4 h-4" />
      </button>
      {open && (
        <>
          {/* Invisible backdrop to catch clicks anywhere */}
          <div
            className="fixed inset-0 z-40 cursor-default"
            style={{ background: 'transparent' }}
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            onPointerDown={(e) => { e.stopPropagation(); setOpen(false); }}
          />
          <div className="absolute right-0 top-full mt-2 z-50 w-56 bg-gray-900/95 backdrop-blur-md
                          border border-white/10 rounded-lg shadow-xl p-3">
            <h4 className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-2">
              Keyboard Shortcuts
            </h4>
            <div className="space-y-1.5">
              {shortcuts.map((s) => (
                <div key={s.key} className="flex items-center justify-between text-xs">
                  <span className="text-white/50">{s.description}</span>
                  <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white/70 font-mono text-[10px]">
                    {s.key}
                  </kbd>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SpatialNavbar;
